import React, { useState } from 'react'

import Box from 'components_new/atoms/Box'
import Tag from 'components_new/atoms/Tag'

import ErrorPlaceholder from 'components_new/molecules/ErrorPlaceholder'
import SearchField from 'components_new/molecules/SearchField'
import SimpleTable from 'components_new/molecules/SimpleTable'

import { Highlighted } from 'components_new/organisms/StandardSearch/utils'

import PageLayout from 'components_new/templates/PageLayout'

import { useTranslation } from 'language/translator'
import { useInternalAccounts } from 'redux/hooks/internal/Accounts'

import { InternalAccount } from 'types/GlobalInternalAccount'

import { getParsedDate } from 'utils/dateParser'

/** A match on the user's name, email or customer. */
const matches = (account: InternalAccount, search: string) => {
  const query = search.toLowerCase()

  return [account.full_name, account.email, account.customer.name].some(
    (value) => value.toLowerCase().includes(query)
  )
}

const UsersPage = () => {
  const { accounts, loading, error } = useInternalAccounts()
  const { lang, t, translateEnum } = useTranslation()

  const [search, setSearch] = useState('')

  const filteredAccounts = accounts.filter((account) =>
    matches(account, search)
  )

  return (
    <PageLayout
      topbar={{
        breadcrumbs: [{ title: 'Admin' }, { title: 'Våra användare' }]
      }}
      variant="static"
    >
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, mb: 1 }}>
        <SearchField
          disabled={loading}
          onClear={() => setSearch('')}
          onSearch={setSearch}
          placeholder={'Sök användare, email eller kund'}
          size="small"
          value={search}
        />
      </Box>
      {error ? (
        <ErrorPlaceholder code={500} />
      ) : (
        <SimpleTable
          columns={[
            { title: 'Användare', width: '30%' },
            { title: 'Kund' },
            { title: 'Roll' },
            { align: 'right', title: 'Senast sedd' }
          ]}
          hover={true}
          loading={loading}
          rows={filteredAccounts.map((account) => {
            const role = account.company_group_id
              ? t('users', 'company_group_user')
              : translateEnum('account_roles', account.role).title

            return {
              cells: [
                <Highlighted
                  key={0}
                  text={account.full_name}
                  highlight={search}
                />,
                <Highlighted
                  key={1}
                  text={account.customer.name}
                  highlight={search}
                />,
                <Tag key={2} label={role} />,
                <React.Fragment key={3}>
                  {account.last_seen_at
                    ? getParsedDate(account.last_seen_at, lang, {
                      time: 'full'
                    })
                    : '–'}
                </React.Fragment>
              ],
              href: `/internal/users/${account.id}`,
              id: account.id,
              sortValues: [
                { value: account.full_name },
                { value: account.customer.name },
                { value: role },

                // An account that has never been seen sorts as the earliest
                // possible visit.
                {
                  value: account.last_seen_at
                    ? new Date(account.last_seen_at)
                    : 0
                }
              ]
            }
          })}
          sx={{ flex: 1 }}
        />
      )}
    </PageLayout>
  )
}

export default UsersPage
